import { inject, Injectable } from '@angular/core';
import { ProfileType, VerticalType } from '@rma/generic/domain/types';
import { CurrentProfileContext, ProfileContextProjectionService, ProfileIdentifier } from '@rma/generic/ng/user';
import { Observable } from 'rxjs';

/**
 * PublicProfileContextService
 *
 * Public-facing facade over the shared {@link ProfileContextProjectionService}. Every stream delegates to the shared
 * projection - the same thin-delegate shape dashboard and promoter use - so the profile derivations live in one place.
 */
@Injectable({
  providedIn: 'root',
})
export class PublicProfileContextService {
  private readonly projection = inject(ProfileContextProjectionService);

  public get nullableProfileContext$(): Observable<CurrentProfileContext | null> {
    return this.projection.nullableProfileContext$;
  }

  public get profileContext$(): Observable<CurrentProfileContext> {
    return this.projection.profileContext$;
  }

  public get profileType$(): Observable<ProfileType> {
    return this.projection.profileType$;
  }

  public get profileCode$(): Observable<string> {
    return this.projection.profileCode$;
  }

  public get profileUrl$(): Observable<string> {
    return this.projection.profileUrl$;
  }

  public get primaryVerticalType$(): Observable<VerticalType> {
    return this.projection.primaryVerticalType$;
  }

  public get shortName$(): Observable<string> {
    return this.projection.shortName$;
  }

  public get profileImage$(): Observable<string> {
    return this.projection.profileImage$;
  }

  public get parentName$(): Observable<string> {
    return this.projection.parentName$;
  }

  public get isIndividual$(): Observable<boolean> {
    return this.projection.isIndividual$;
  }

  public get isTeam$(): Observable<boolean> {
    return this.projection.isTeam$;
  }

  public get profileIdentifier$(): Observable<ProfileIdentifier> {
    return this.projection.profileIdentifier$;
  }

  public get nullableProfileIdentifier$(): Observable<ProfileIdentifier | null> {
    return this.projection.nullableProfileIdentifier$;
  }
}
